import React, { useRef, useState, useEffect } from "react";
import { Camera, BarChart3, Bell } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

export default function FeatureCards() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.2 });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const SkeletonCard = () => (
    <div className="p-6 min-h-[300px] border border-gray-700 rounded-xl bg-[#1b2a1e] animate-pulse">
      <div className="w-10 h-10 mb-4 bg-gray-700 rounded-full" />
      <div className="h-5 w-2/3 mb-3 bg-gray-700 rounded" />
      <div className="h-4 w-full mb-1 bg-gray-700 rounded" />
      <div className="h-4 w-5/6 mb-1 bg-gray-700 rounded" />
      <div className="h-4 w-2/3 bg-gray-700 rounded" />
    </div>
  );

  return (
    <section
      className="bg-[#0e1a10] text-white px-4 sm:px-6 py-12 font-manrope"
      ref={containerRef}
    >
      <div className="max-w-7xl mx-auto">
        {/* Heading with Skeleton */}
        {loading ? (
          <div className="animate-pulse space-y-3 mb-8">
            <div className="h-4 w-1/4 bg-gray-700 rounded mx-auto lg:mx-0" />
            <div className="h-6 sm:h-8 md:h-10 w-3/4 bg-gray-700 rounded mx-auto lg:mx-0" />
            <div className="h-4 w-full sm:w-5/6 bg-gray-700 rounded mx-auto lg:mx-0" />
          </div>
        ) : (
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <h3 className="text-sm sm:text-lg md:text-xl font-semibold mb-2 text-center lg:text-left text-green-400 uppercase tracking-widest">
              Key Features
            </h3>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-extrabold text-center lg:text-left leading-snug">
              Unlock Your Potential with{" "}
              <span className="text-green-400">FitTrack’s</span> Intelligent Tools
            </h2>
            <p className="mt-4 text-gray-300 text-base sm:text-lg md:text-base text-center lg:text-left">
              FitTrack empowers you with the tools and insights you need to transform your health and fitness journey. From intelligent food logging to personalized reminders, we've got you covered.
            </p>
          </motion.div>
        )}

        {/* Cards Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10 mt-10 sm:mt-12">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              {/* Card 1 */}
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ delay: 0.1 }}
                onClick={() => navigate("/FeatureAiFoodDetection")}
                className="p-6 min-h-[300px] border border-gray-700 rounded-xl bg-[#1b2a1e] hover:border-green-500 transition-all duration-300 shadow-sm hover:shadow-lg cursor-pointer"
              >
                <div className="mb-5 text-green-400">
                  <Camera size={36} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3">
                  AI-Powered Food Detection
                </h3>
                <p className="text-sm lg:text-base text-gray-300 leading-relaxed">
                  Simply point your camera at your meal — our AI detects the food and instantly logs
                  calories and nutrition data for you. Perfect for busy lifestyles and quick tracking.
                </p>
              </motion.div>

              {/* Card 2 */}
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ delay: 0.3 }}
                onClick={() => navigate("/calorie-tracking")}
                className="p-6 min-h-[300px] border border-gray-700 rounded-xl bg-[#1b2a1e] hover:border-green-500 transition-all duration-300 shadow-sm hover:shadow-lg cursor-pointer"
              >
                <div className="mb-5 text-green-400">
                  <BarChart3 size={36} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3">
                  Comprehensive Calorie Tracking
                </h3>
                <p className="text-sm lg:text-base text-gray-300 leading-relaxed">
                  Get detailed insights into your daily intake with beautiful charts and macro-nutrient
                  breakdowns. Stay informed and in control of your health journey.
                </p>
              </motion.div>

              {/* Card 3 */}  
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ delay: 0.5 }}
                onClick={() => navigate("/personal-reminders")}
                className="p-6 min-h-[300px] border border-gray-700 rounded-xl bg-[#1b2a1e] hover:border-green-500 transition-all duration-300 shadow-sm hover:shadow-lg cursor-pointer"
              >
                <div className="mb-5 text-green-400">
                  <Bell size={36} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3">
                  Personalized Reminders
                </h3>
                <p className="text-sm lg:text-base text-gray-300 leading-relaxed">
                  Never miss a workout or meal again. Set custom reminders tailored to your goals,
                  schedule, and fitness level. Motivation just when you need it.
                </p>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
